import { ErrorBoundary } from "@app/components/ErrorBoundary.tsx";
import { Footer } from "@app/components/Footer.tsx";
import { Header } from "@app/components/Header.tsx";
import { SearchOverlay } from "@app/components/SearchOverlay.tsx";
import { type Manifest, loadManifest } from "@app/lib/manifest.ts";
import { navigateTo, useRoute } from "@app/lib/router.ts";
import { ViewModeProvider } from "@app/lib/viewMode.tsx";
import { Landing } from "@app/routes/Landing.tsx";
import { List } from "@app/routes/List.tsx";
import { Reader } from "@app/routes/Reader.tsx";
import { useEffect, useState } from "react";

export function App() {
  const route = useRoute();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    loadManifest().then(setManifest, setLoadError);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (loadError) {
    return (
      <div className="p-12 text-center">
        <h1 className="text-2xl mb-4">Couldn't load library</h1>
        <p className="text-ink-200/70">{loadError.message}</p>
      </div>
    );
  }

  if (!manifest) {
    return <div className="p-12 text-center text-ink-200/70 italic">Loading library…</div>;
  }

  return (
    <ViewModeProvider>
      <ErrorBoundary>
        <Header onOpenSearch={() => setSearchOpen(true)} />
        {route.kind === "landing" && (
          <Landing manifest={manifest} onOpenSearch={() => setSearchOpen(true)} />
        )}
        {route.kind === "list" && (
          <List manifest={manifest} onOpenSearch={() => setSearchOpen(true)} />
        )}
        {route.kind === "doc" && <Reader manifest={manifest} docId={route.id} />}
        <Footer />
        <SearchOverlay
          docs={manifest.docs}
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelect={(d) => navigateTo({ kind: "doc", id: d.id })}
        />
      </ErrorBoundary>
    </ViewModeProvider>
  );
}
